import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faLocationDot, faWallet, faCamera, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import './login.css';

// Phone helpers — the PhoneInput may include spaces/dashes; the backend wants
// a bare +E.164. A value that's only the dial code counts as empty.
const cleanPhone = (v) => String(v || '').replace(/[\s-]/g, '');
const isRealPhone = (v) => /^\+\d{9,15}$/.test(cleanPhone(v));

// App store badges — rendered in the hero (desktop) and again under the login
// card (mobile only, via CSS), so phone visitors see the apps without scrolling.
function StoreBadges() {
  return (
    <div className="portal-login-store-badges">
      <a
        className="portal-login-store-badge"
        href="https://apps.apple.com/ae/app/holiday-home-cleaning/id6749137403"
        target="_blank"
        rel="noreferrer"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.03 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702" />
        </svg>
        <span className="portal-login-store-badge-text">
          <span className="portal-login-store-badge-small">Download on the</span>
          <span className="portal-login-store-badge-big">App Store</span>
        </span>
      </a>
      <a
        className="portal-login-store-badge"
        href="https://play.google.com/store/apps/details?id=com.anonymous.hhcmobileapp"
        target="_blank"
        rel="noreferrer"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
        </svg>
        <span className="portal-login-store-badge-text">
          <span className="portal-login-store-badge-small">Get it on</span>
          <span className="portal-login-store-badge-big">Google Play</span>
        </span>
      </a>
      {/* The business WhatsApp line (2026-08-04) — same badge shape so
          the row reads as one family, WhatsApp green on the glyph. */}
      <a
        className="portal-login-store-badge portal-login-store-badge--whatsapp"
        href="https://wa.me/971585891289?text=Hi!%20I%27d%20like%20to%20know%20more%20about%20your%20cleaning%20services."
        target="_blank"
        rel="noreferrer"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
        </svg>
        <span className="portal-login-store-badge-text">
          <span className="portal-login-store-badge-small">Contact us</span>
          <span className="portal-login-store-badge-big">On WhatsApp</span>
        </span>
      </a>
    </div>
  );
}

const EMPTY_ONBOARD = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  whatsapp: '',
  propertyRange: '',
  location: '',
};

// Post-login destination carried by ?next=… (set by the payment-return flow /
// ProtectedRoute). Same-origin relative paths only — anything else (absolute
// URLs, protocol-relative //host) is ignored to keep this an internal hop.
const safeNextUrl = () => {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
};

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // OTP login (email / whatsapp). otpChannel === null => password mode.
  const [otpChannel, setOtpChannel] = useState(null);
  const [otpStep, setOtpStep] = useState(1);
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);

  // Onboarding ("Get started") modal
  const [showOnboard, setShowOnboard] = useState(false);
  const [onboard, setOnboard] = useState(EMPTY_ONBOARD);
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [onboardError, setOnboardError] = useState('');
  const [onboardDone, setOnboardDone] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get('/api/login/auth/check', { withCredentials: true });
        const user = res.data.user;

        // ✅ Restore localStorage if needed
        localStorage.setItem('userId', user.id);
        localStorage.setItem('firstName', user.first_name);
        localStorage.setItem('userRole', user.user_role);
        if (user.user_role === 'client' && user.client_id) {
          localStorage.setItem('clientId', user.client_id);
        }

        // ✅ Redirect: honor a pending ?next= (payment-return round-trip),
        // else based on role
        window.location.href = safeNextUrl() || (user.user_role === 'client' ? '/client/home' : '/');
      } catch (err) {
        // Not logged in — clean up
        localStorage.removeItem('userRole');
        localStorage.removeItem('firstName');
        localStorage.removeItem('userId');
        localStorage.removeItem('clientId');
        sessionStorage.clear();
      }
    };

    checkAuth();
  }, []);

  // Shared post-login success flow (used by password + OTP verify)
  const handleLoginSuccess = (user) => {
    // Store user info in localStorage
    localStorage.setItem('firstName', user.first_name);
    localStorage.setItem('userRole', user.user_role);
    localStorage.setItem('userId', user.id);

    if (user.user_role === 'client' && user.client_id) {
      localStorage.setItem('clientId', user.client_id);
    }

    // 🔁 Force full reload to refresh state & sidebar. A pending ?next=
    // (payment-return round-trip) wins over the role default so the client
    // lands back on the page that shows the verified payment outcome.
    window.location.href = safeNextUrl() || (user.user_role === 'client' ? '/client/home' : '/');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await axios.post(
        '/api/login',
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );

      handleLoginSuccess(response.data.user);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Invalid email or password');
      } else {
        setError('An error occurred. Please try again.');
      }
    }
  };

  const openOtp = (channel, prefillIdentifier = '') => {
    setOtpChannel(channel);
    setOtpStep(1);
    setIdentifier(prefillIdentifier);
    setCode('');
    setOtpError('');
    setOtpMessage('');
  };

  // Deep entry from registration (PLAN_registration.md): a signup that hits
  // "account already exists" bounces here as /login?otp=whatsapp&phone=… —
  // straight into the WhatsApp code flow with the SAME number filled in, so
  // the person types nothing twice.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('otp') === 'whatsapp') {
      openOtp('whatsapp', params.get('phone') || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backToPassword = () => {
    setOtpChannel(null);
    setOtpStep(1);
    setIdentifier('');
    setCode('');
    setOtpError('');
    setOtpMessage('');
  };

  const normalizedIdentifier = () =>
    otpChannel === 'email' ? identifier.trim().toLowerCase() : cleanPhone(identifier);

  // Calls otp/start. Returns true when a code was sent.
  const requestCode = async () => {
    setOtpError('');
    setOtpMessage('');
    setOtpBusy(true);

    try {
      const response = await axios.post(
        '/api/login/otp/start',
        { channel: otpChannel, identifier: normalizedIdentifier() },
        { withCredentials: true }
      );
      setOtpMessage(response.data?.message || 'Code sent.');
      return true;
    } catch (error) {
      // 404 found:false, 429, 502, 400 all carry a clear backend message
      setOtpError(error.response?.data?.message || 'Could not send code. Please try again.');
      return false;
    } finally {
      setOtpBusy(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    // PhoneInput can't be browser-required — a bare "+971" would otherwise
    // submit. Validate here with the same rule the Team pages use.
    if (otpChannel === 'whatsapp' && !isRealPhone(identifier)) {
      setOtpError('Enter a valid WhatsApp number, including the country code.');
      return;
    }
    const sent = await requestCode();
    if (sent) {
      setCode('');
      setOtpStep(2);
    }
  };

  const handleResend = async () => {
    setCode('');
    await requestCode();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setOtpError('');
    setOtpBusy(true);

    try {
      const response = await axios.post(
        '/api/login/otp/verify',
        { channel: otpChannel, identifier: normalizedIdentifier(), code: code.trim() },
        { withCredentials: true }
      );
      handleLoginSuccess(response.data.user);
    } catch (error) {
      setOtpError(error.response?.data?.message || 'Could not verify the code. Please try again.');
    } finally {
      setOtpBusy(false);
    }
  };

  // ---- Onboarding modal ----

  // Kept for the legacy inquiry modal (reachable only if we ever re-link it);
  // the hero CTA now goes straight to /register.
  // eslint-disable-next-line no-unused-vars
  const openOnboard = () => {
    setOnboard(EMPTY_ONBOARD);
    setOnboardError('');
    setOnboardDone(false);
    setShowOnboard(true);
  };

  const closeOnboard = () => {
    if (onboardBusy) return;
    setShowOnboard(false);
  };

  const setOnboardField = (field) => (e) => {
    const value = e.target.value;
    setOnboard((prev) => ({ ...prev, [field]: value }));
  };

  // PhoneInput passes the phone string directly, not an event.
  const setOnboardPhone = (field) => (phone) => {
    setOnboard((prev) => ({ ...prev, [field]: phone }));
  };

  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    setOnboardError('');
    if (!isRealPhone(onboard.phone)) {
      setOnboardError('Enter a valid phone number, including the country code.');
      return;
    }
    setOnboardBusy(true);

    try {
      await axios.post(
        '/api/login/onboard',
        {
          firstName: onboard.firstName.trim(),
          lastName: onboard.lastName.trim(),
          companyName: onboard.companyName.trim(),
          email: onboard.email.trim().toLowerCase(),
          phone: cleanPhone(onboard.phone),
          // A bare dial code means "not provided" — WhatsApp is optional.
          whatsapp: isRealPhone(onboard.whatsapp) ? cleanPhone(onboard.whatsapp) : '',
          propertyRange: onboard.propertyRange,
          location: onboard.location.trim(),
        },
        { withCredentials: true }
      );
      setOnboardDone(true);
    } catch (error) {
      setOnboardError(
        error.response?.data?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setOnboardBusy(false);
    }
  };

  return (
    <div className="portal-login-page">
      <div className="portal-login-split">
        <aside className="portal-login-hero">
          <div className="portal-login-hero-inner">
            {/* Logo = the way back to the main site (2026-08-04). */}
            <a href="https://holidayhomecleaning.ae" className="portal-login-home-link" title="Back to holidayhomecleaning.ae">
              <img src="/logo.png" alt="Holiday Home Cleaning" className="portal-login-hero-logo" />
            </a>

            <div className="portal-login-hero-copy">
              <span className="portal-login-eyebrow">
                <span className="portal-login-eyebrow-dot" />
                Dubai&rsquo;s Airbnb Cleaning Specialist
              </span>
              <h2 className="portal-login-hero-headline">
                Holiday home cleaning,{' '}
                <span className="portal-login-hero-accent">handled.</span>
              </h2>
              <p className="portal-login-hero-subhead">
                Book, track, pay and report — all in one place.
              </p>
            </div>

            <ul className="portal-login-props">
              <li className="portal-login-prop">
                <span className="portal-login-prop-icon">
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </span>
                <span>Book cleanings in seconds</span>
              </li>
              <li className="portal-login-prop">
                <span className="portal-login-prop-icon">
                  <FontAwesomeIcon icon={faLocationDot} />
                </span>
                <span>Live tracking of every visit</span>
              </li>
              <li className="portal-login-prop">
                <span className="portal-login-prop-icon">
                  <FontAwesomeIcon icon={faWallet} />
                </span>
                <span>Simple wallet payments</span>
              </li>
              <li className="portal-login-prop portal-login-prop-desktop">
                <span className="portal-login-prop-icon">
                  <FontAwesomeIcon icon={faCamera} />
                </span>
                <span>Instant photo reports</span>
              </li>
            </ul>

            {/* CTA + floating mock card share one row (the owner: no separate row) */}
            <div className="portal-login-hero-action-row">
              {/* Straight to self-serve registration (2026-08-04) — the
                  old inquiry modal is superseded by a real signup. */}
              <Link to="/register" className="portal-login-hero-cta">
                Get started <span className="portal-login-hero-cta-arrow">→</span>
              </Link>

              <div className="portal-login-mock-card" aria-hidden="true">
                <div className="portal-login-mock-head">
                  <span className="portal-login-mock-status">
                    <span className="portal-login-mock-dot" />
                    Confirmed
                  </span>
                  <span className="portal-login-mock-time">Today · 11:00</span>
                </div>
                <div className="portal-login-mock-title">All Inclusive Cleaning</div>
                <div className="portal-login-mock-sub">Palm Jumeirah · 3 Bedrooms</div>
              </div>
            </div>

            <div className="portal-login-stores">
              <span className="portal-login-stores-label">Get the app</span>
              <StoreBadges />
            </div>
          </div>
        </aside>

        <div className="portal-login-container">
          <div className="portal-login-card">
            <div className="portal-login-logo-wrapper">
              <a href="https://holidayhomecleaning.ae" className="portal-login-home-link" title="Back to holidayhomecleaning.ae">
                <img src="/logo.png" alt="Holiday Home Cleaning" className="portal-login-logo" />
              </a>
            </div>

            <h1 className="portal-login-title">
              Welcome <span className="portal-login-title-accent">back.</span>
            </h1>
            <p className="portal-login-subtitle">
              {otpChannel
                ? 'Log in with a one-time code.'
                : 'Log in to access your portal.'}
            </p>

            {otpChannel === null && (
              <>
                <form onSubmit={handleLogin} className="portal-login-form">
                  <div className="portal-login-form-group">
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="portal-login-input"
                    />
                  </div>

                  <div className="portal-login-form-group">
                    <div className="portal-login-password-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="portal-login-input"
                      />
                      {/* type="button" matters: a bare <button> inside a form
                          defaults to submit, so revealing the password would
                          fire the login attempt. */}
                      <button
                        type="button"
                        className="portal-login-password-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="portal-login-button">
                    Login
                  </button>

                  {error && (
                    <p className="portal-login-error-message">{error}</p>
                  )}
                </form>

                <div className="portal-login-footer">
                  <Link to="/forgot-password" className="portal-login-reset-link">
                    Forgot password?
                  </Link>
                </div>

                <div className="portal-login-divider">or</div>

                <div className="portal-login-alt-buttons">
                  <button
                    type="button"
                    className="portal-login-alt-button"
                    onClick={() => openOtp('email')}
                  >
                    Log in with Email
                  </button>
                  <button
                    type="button"
                    className="portal-login-alt-button"
                    onClick={() => openOtp('whatsapp')}
                  >
                    Log in with WhatsApp
                  </button>
                  {/* Self-serve signup (PLAN_registration.md) — same look as
                      the Sign in button (2026-08-04). */}
                  <Link to="/register" className="portal-login-button portal-login-button--link">
                    Register in few clicks
                  </Link>
                </div>
              </>
            )}

            {otpChannel !== null && otpStep === 1 && (
              <form onSubmit={handleSendCode} className="portal-login-form">
                <div className="portal-login-form-group">
                  <label className="portal-login-label">
                    {otpChannel === 'email' ? 'Email' : 'WhatsApp number'}
                  </label>
                  {otpChannel === 'email' ? (
                    <input
                      type="email"
                      placeholder="Email"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      required
                      className="portal-login-input"
                    />
                  ) : (
                    <PhoneInput
                      defaultCountry="ae"
                      value={identifier}
                      onChange={(phone) => setIdentifier(phone)}
                      placeholder="WhatsApp number"
                      className="portal-login-phone-wrapper"
                      inputClassName="portal-login-phone-input"
                    />
                  )}
                </div>

                <button type="submit" className="portal-login-button" disabled={otpBusy}>
                  {otpBusy ? 'Sending…' : 'Send code'}
                </button>

                {otpError && (
                  <p className="portal-login-error-message">{otpError}</p>
                )}
              </form>
            )}

            {otpChannel !== null && otpStep === 2 && (
              <form onSubmit={handleVerify} className="portal-login-form">
                {otpMessage && (
                  <p className="portal-login-success-message">{otpMessage}</p>
                )}

                <div className="portal-login-form-group">
                  <label className="portal-login-label">6-digit code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="Enter the 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                    className="portal-login-input"
                  />
                </div>

                <button type="submit" className="portal-login-button" disabled={otpBusy}>
                  {otpBusy ? 'Verifying…' : 'Verify & sign in'}
                </button>

                {otpError && (
                  <p className="portal-login-error-message">{otpError}</p>
                )}

                <button
                  type="button"
                  className="portal-login-link-button"
                  onClick={handleResend}
                  disabled={otpBusy}
                >
                  Resend code
                </button>
              </form>
            )}

            {otpChannel !== null && (
              <div className="portal-login-footer">
                <button
                  type="button"
                  className="portal-login-link-button"
                  onClick={backToPassword}
                >
                  ← Back to password login
                </button>
              </div>
            )}
          </div>

          {/* Mobile-only: apps visible right with the login, no scroll needed */}
          <div className="portal-login-mobile-stores">
            <span className="portal-login-stores-label">Get the app</span>
            <StoreBadges />
          </div>
        </div>
      </div>

      {showOnboard && (
        <div className="portal-login-modal-overlay" onClick={closeOnboard}>
          <div
            className="portal-login-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Get started"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="portal-login-modal-close"
              aria-label="Close"
              onClick={closeOnboard}
            >
              ×
            </button>

            {onboardDone ? (
              <div className="portal-login-modal-success">
                <h2 className="portal-login-modal-title">Thanks!</h2>
                <p className="portal-login-modal-text">
                  Thanks! Our team will reach out shortly.
                </p>
                <button
                  type="button"
                  className="portal-login-button"
                  onClick={closeOnboard}
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h2 className="portal-login-modal-title">Get started</h2>
                <p className="portal-login-modal-text">
                  Tell us a little about you and we&rsquo;ll set you up.
                </p>

                <form onSubmit={handleOnboardSubmit} className="portal-login-form">
                  <div className="portal-login-modal-row">
                    <div className="portal-login-form-group">
                      <label className="portal-login-label">First name *</label>
                      <input
                        type="text"
                        value={onboard.firstName}
                        onChange={setOnboardField('firstName')}
                        required
                        className="portal-login-input"
                      />
                    </div>
                    <div className="portal-login-form-group">
                      <label className="portal-login-label">Last name *</label>
                      <input
                        type="text"
                        value={onboard.lastName}
                        onChange={setOnboardField('lastName')}
                        required
                        className="portal-login-input"
                      />
                    </div>
                  </div>

                  <div className="portal-login-form-group">
                    <label className="portal-login-label">Company name</label>
                    <input
                      type="text"
                      value={onboard.companyName}
                      onChange={setOnboardField('companyName')}
                      className="portal-login-input"
                    />
                  </div>

                  <div className="portal-login-form-group">
                    <label className="portal-login-label">Email *</label>
                    <input
                      type="email"
                      value={onboard.email}
                      onChange={setOnboardField('email')}
                      required
                      className="portal-login-input"
                    />
                  </div>

                  <div className="portal-login-modal-row">
                    <div className="portal-login-form-group">
                      <label className="portal-login-label">Phone *</label>
                      <PhoneInput
                        defaultCountry="ae"
                        value={onboard.phone}
                        onChange={setOnboardPhone('phone')}
                        placeholder="Phone number"
                        className="portal-login-phone-wrapper"
                        inputClassName="portal-login-phone-input"
                      />
                    </div>
                    <div className="portal-login-form-group">
                      <label className="portal-login-label">WhatsApp</label>
                      <PhoneInput
                        defaultCountry="ae"
                        value={onboard.whatsapp}
                        onChange={setOnboardPhone('whatsapp')}
                        placeholder="If different"
                        className="portal-login-phone-wrapper"
                        inputClassName="portal-login-phone-input"
                      />
                    </div>
                  </div>

                  <div className="portal-login-modal-row">
                    <div className="portal-login-form-group">
                      <label className="portal-login-label">Properties *</label>
                      <select
                        value={onboard.propertyRange}
                        onChange={setOnboardField('propertyRange')}
                        required
                        className="portal-login-input portal-login-select"
                      >
                        <option value="" disabled>
                          Select…
                        </option>
                        <option value="1-5">1–5</option>
                        <option value="6-15">6–15</option>
                        <option value="16-30">16–30</option>
                        <option value="30+">30+</option>
                      </select>
                    </div>
                    <div className="portal-login-form-group">
                      <label className="portal-login-label">Location *</label>
                      <input
                        type="text"
                        placeholder="Area / city"
                        value={onboard.location}
                        onChange={setOnboardField('location')}
                        required
                        className="portal-login-input"
                      />
                    </div>
                  </div>

                  <button type="submit" className="portal-login-button" disabled={onboardBusy}>
                    {onboardBusy ? 'Sending…' : 'Get Started'}
                  </button>

                  {onboardError && (
                    <p className="portal-login-error-message">{onboardError}</p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
